import {VersionName} from '@github-ui/copilot-chat/components/VersionName'
import {deserializeDraftIssueProjects} from '@github-ui/copilot-chat-helpers/draft-issue-serialization'
import {disableStreamingFadeIn} from '@github-ui/copilot-markdown'
import {sendEvent} from '@github-ui/hydro-analytics'
import {load} from '@github-ui/js-yaml'
import {Spinner} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {getRevisionNumber} from '../../../components/ContentPreview/content-preview-types'
import {useContentPreview} from '../../../components/ContentPreview/ContentPreviewContext'
import {IssueIcon} from '../../../components/ContentPreview/create-issue-preview/IssueIcon'
import {IssueTree} from '../../../components/ContentPreview/create-issue-preview/IssueTree'
import {usePreProcessedDraftIssueContent} from '../../../components/ContentPreview/create-issue-preview/use-pre-processed-new-issue'
import {useContentPreviewBlockContext} from '../../ContentPreviewBlockContext'
import {useSyncState} from '../../hooks/use-sync-state'
import {useDraftIssueReferenceValidation} from '../hooks/use-draft-issue-reference-validation'
import type {DraftIssue as DraftIssueType} from '../types'

export const DraftIssue = {
  /** Being extremely defensive about the types here allows us to safely handle partial data and bad model results. */
  fromUnknown: (data: unknown): DraftIssueType | null => {
    if (typeof data !== 'object' || data == null) return null

    // there's probably a library that could do this less tediously
    const item: DraftIssueType = {}

    if ('title' in data && typeof data.title === 'string') {
      item.title = data.title
    } else if ('name' in data && typeof data.name === 'string') {
      item.title = data.name
    }
    if ('tag' in data && typeof data.tag === 'string') item.tag = data.tag

    if (
      'template-form-values' in data &&
      typeof data['template-form-values'] === 'object' &&
      data['template-form-values'] != null
    ) {
      // Trim each value in the template form values, as yaml parsing can leave trailing newlines.
      item.templateFormValues = Object.fromEntries(
        Object.entries(data['template-form-values'] as Record<string, string>).map(([key, value]) => [
          key,
          typeof value === 'string' ? value.trim() : value,
        ]),
      )
    }

    if ('description' in data && typeof data.description === 'string') item.body = data.description
    if ('labels' in data && Array.isArray(data.labels)) item.labels = data.labels.filter(el => typeof el === 'string')
    if ('assignees' in data && Array.isArray(data.assignees))
      item.assignees = data.assignees.filter(el => typeof el === 'string')
    if ('repository' in data && typeof data.repository === 'string') item.repository = data.repository
    if ('template' in data && typeof data.template === 'string') item.template = data.template
    if ('projects' in data && Array.isArray(data.projects))
      item.projects = data.projects.filter(el => typeof el === 'string' || (typeof el === 'object' && el != null))
    if ('milestone' in data && typeof data.milestone === 'string') item.milestone = data.milestone
    if ('state' in data && typeof data.state === 'string') item.state = data.state
    if ('parentTag' in data && typeof data.parentTag === 'string') item.parentTag = data.parentTag
    if ('issueNumber' in data && typeof data.issueNumber === 'number') {
      item.issueNumber = data.issueNumber
    }
    if ('blockedByTags' in data && Array.isArray(data.blockedByTags)) {
      item.blockedByTags = data.blockedByTags.filter(el => typeof el === 'string')
    }
    if ('isBlockingTags' in data && Array.isArray(data.isBlockingTags)) {
      item.isBlockingTags = data.isBlockingTags.filter(el => typeof el === 'string')
    }
    if ('bodyVersion' in data && typeof data.bodyVersion === 'string') {
      item.bodyVersion = data.bodyVersion
    }
    if ('issueType' in data && typeof data.issueType === 'string') {
      item.issueType = data.issueType
    }
    if ('type' in data && typeof data.type === 'string') {
      item.type = data.type
    }

    return item
  },
}

const parseIssueData = (yaml: string) => {
  try {
    const result = load(yaml)
    return DraftIssue.fromUnknown(result)
  } catch {
    return null
  }
}

export interface DraftIssueBlockProps {
  yaml: string
  isStreaming?: boolean
}

export function DraftIssueBlock({yaml, isStreaming}: DraftIssueBlockProps) {
  const [fromStreaming, setFromStreaming] = useState(isStreaming)

  useEffect(() => {
    if (isStreaming) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFromStreaming(true)
    }
  }, [isStreaming])

  const {updateItem, openItem, openPreviewPane, closeItem, versionedItems, items} = useContentPreview()
  const {messageId, messageIndex, autoOpenPreviewPane, hasAutoOpenedPreviewPaneRef, setHasAutoOpenedPreviewPane} =
    useContentPreviewBlockContext()

  // pre-process the raw YAML content to transform image URLs etc before parsing
  yaml = usePreProcessedDraftIssueContent(yaml)

  // Employ `useMemo + useRef` instead of `useState` to avoid asynchronous state updates
  // Since the order of versionedItems matters, we need to ensure that we call `updateItem` with the latest data
  // immediately after the YAML is changed.
  const issue = useSyncState(yaml, parseIssueData, {})

  // Validate draft issue against references using shared hook
  useDraftIssueReferenceValidation({
    issues: [issue],
    isStreaming,
    isIssueTree: false,
  })

  useEffect(() => sendEvent('dotcom_chat.activate', {target: 'ISSUE_DRAFT_BLOCK_RENDERED', mode: 'immersive'}), [])

  const draftIssueType =
    issue.type == null && isStreaming ? undefined : issue.type === 'draft-update-issue' ? 'update-issue' : 'new-issue'
  const id =
    issue.tag != null && draftIssueType != null
      ? (`${draftIssueType}:${issue.tag}#${messageIndex}` as const)
      : undefined
  const version = id != null ? getRevisionNumber(id, versionedItems, items) : null
  const item = id != null ? items.get(id) : undefined
  const repository =
    !issue.repository && draftIssueType != null && item?.type === draftIssueType
      ? item.repository || ''
      : issue.repository

  const openDraftIssue = useCallback(
    ({selectItem, event}: {selectItem: boolean; event?: React.MouseEvent}) => {
      if (id != null) {
        openItem(id, selectItem)

        if (selectItem || (autoOpenPreviewPane && !hasAutoOpenedPreviewPaneRef.current)) {
          openPreviewPane(event?.currentTarget as HTMLElement)
          // Copied from FileBlock.tsx
          setHasAutoOpenedPreviewPane(true)
        }

        sendEvent('dotcom_chat.activate', {target: 'BROWSER_DRAFT_ISSUE_OPENED', mode: 'immersive'})
      }
    },
    [id, openItem, autoOpenPreviewPane, hasAutoOpenedPreviewPaneRef, openPreviewPane, setHasAutoOpenedPreviewPane],
  )

  const normalizedProjects = useMemo(() => {
    return deserializeDraftIssueProjects(issue.projects)
  }, [issue.projects])

  // Side effect that only concerns about the changes from streaming content
  useEffect(() => {
    if (id != null && draftIssueType != null) {
      if (draftIssueType === 'update-issue' && repository && issue.issueNumber) {
        // Because we may be drafting changes for an issue that was just saved, there may
        // already be a tab open for the `issue:*` preview type. Close it.
        closeItem(`issue:${repository}/${issue.issueNumber}`)
      }

      updateItem({
        messageId,
        name: issue.title ?? '',
        body: issue.body,
        bodyVersion: issue.bodyVersion,
        repository,
        id,
        type: draftIssueType,
        tag: issue.tag ?? '',
        parentTag: issue.parentTag,
        isStreaming,
        template: issue.template,
        issueType: issue.issueType,
        milestone: issue.milestone,
        // Copilot will return arrays in arbitrary order. We need stability for versioning to work.
        assignees: issue.assignees?.sort(),
        labels: issue.labels?.sort(),
        projects: normalizedProjects,
        rawProjectsInput: normalizedProjects
          .map(project => project.title || '')
          .filter(title => title !== '')
          .sort(),
        isUserEdited: false,
        defaultValuesById: issue.templateFormValues ?? {},
        issueNumber: issue.issueNumber,
      })
    }
  }, [
    closeItem,
    draftIssueType,
    id,
    isStreaming,
    issue.assignees,
    issue.body,
    issue.bodyVersion,
    issue.issueNumber,
    issue.issueType,
    issue.labels,
    issue.milestone,
    issue.parentTag,
    issue.projects,
    issue.tag,
    issue.template,
    issue.templateFormValues,
    issue.title,
    messageId,
    normalizedProjects,
    repository,
    updateItem,
  ])

  // Side effect to automatically open the preview pane if it's the latest version
  useEffect(() => {
    if (id != null && fromStreaming) {
      openDraftIssue({selectItem: true})
    }
  }, [id, openDraftIssue, fromStreaming])

  return (
    <IssueTree subIssueCount={0}>
      <IssueTree.RootItem
        title={issue.title ?? ''}
        leadingVisual={
          isStreaming ? <Spinner size="small" className={disableStreamingFadeIn} /> : <IssueIcon type={'new-issue'} />
        }
        trailingBadge={version != null && <VersionName data-testid="version-label" version={version} />}
        onClick={e => openDraftIssue({selectItem: true, event: e})}
      />
    </IssueTree>
  )
}
